import { Link } from "react-router";
import {
    CheckIcon,
    XMarkIcon,
    ArrowUturnUpIcon,
} from "@heroicons/react/16/solid";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

function ForClinicians() {
    return (
        <>
            <div className="flex h-full px-4 pt-6 pb-48 md:px-10">
                <div className="container mx-auto grid grid-cols-12 gap-x-8 gap-y-6 md:pt-15">
                    <div className="col-span-full md:col-span-7">
                        <div className="mx-auto prose max-w-4xl prose-darkgreen">
                            <p className="text-center text-sm font-medium tracking-[0.32em] text-neutral-400 uppercase md:text-start">
                                For clinicians
                            </p>

                            <h1 className="mb-4 text-center text-6xl font-semibold tracking-wider md:text-start">
                                Grow your practice. We'll handle the admin.
                            </h1>

                            <p className="lead text-center text-neutral-500 md:text-start">
                                Join a calm, professional platform built around
                                UK private practice, with the tools to fill your
                                diary and win back your time.
                            </p>

                            <ul className="list-none ps-0">
                                <li>
                                    <div className="flex items-start gap-2">
                                        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-emerald-950 font-bold">
                                            <CheckIcon className="size-4 shrink-0 stroke-2" />
                                        </span>
                                        New patient bookings, with fee-free
                                        repeat visits to keep your loyal
                                        patients
                                    </div>
                                </li>
                                <li>
                                    <div className="flex items-start gap-2">
                                        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-emerald-950 font-bold">
                                            <CheckIcon className="size-4 shrink-0 stroke-2" />
                                        </span>
                                        Automatic credential & expiry tracking
                                        for HCPC, NMC and GMC registrations
                                    </div>
                                </li>
                                <li>
                                    <div className="flex items-start gap-2">
                                        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-emerald-950 font-bold">
                                            <CheckIcon className="size-4 shrink-0 stroke-2" />
                                        </span>
                                        Smart reminders that cut no-shows
                                    </div>
                                </li>
                            </ul>
                            <div className="not-prose flex gap-4">
                                <Button variant="primary" asChild>
                                    <Link to="/clinician">
                                        List your practice
                                    </Link>
                                </Button>
                                <Button variant="primary-outline" asChild>
                                    <Link to="/clinician">See pricing</Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                    <div className="col-span-full grid md:col-span-5">
                        <Card size="lg" className="my-auto gap-4 md:gap-5">
                            <Badge variant="beige" size="lg">
                                Your practice this week
                            </Badge>
                            <div className="divide-y divide-neutral-200">
                                <div className="flex justify-between py-3">
                                    <span>New patients</span>
                                    <span className="font-semibold">+12</span>
                                </div>
                                <div className="flex justify-between py-3">
                                    <span>Upcoming appointments</span>
                                    <span className="font-semibold">28</span>
                                </div>
                                <div className="flex justify-between py-3">
                                    <span>Credential status</span>
                                    <div className="flex items-center gap-0.5 font-semibold text-emerald-50">
                                        <CheckIcon className="size-4 shrink-0 stroke-2" />{" "}
                                        All valid
                                    </div>
                                </div>
                                <div className="flex justify-between py-3">
                                    <span>Estimated earnings</span>
                                    <span className="font-semibold">
                                        £2,340
                                    </span>
                                </div>
                            </div>
                            <Button
                                variant="success"
                                className="w-full"
                                asChild
                            >
                                <Link to="/clinician">Open dashboard</Link>
                            </Button>
                        </Card>
                    </div>
                </div>
            </div>
            <div className="flex h-full bg-white px-4 pt-6 pb-24 md:px-10">
                <div className="container mx-auto grid grid-cols-12 gap-x-8 gap-y-6 md:pt-15">
                    <div className="col-span-full md:col-span-7">
                        <div className="mx-auto prose max-w-4xl prose-darkgreen">
                            <p className="text-sm font-medium tracking-[0.32em] text-neutral-400 uppercase md:text-start">
                                Getting started
                            </p>

                            <h2 className="mt-0 mb-4 text-4xl font-semibold tracking-wider md:text-start md:text-6xl">
                                From sign-up to your first booking
                            </h2>

                            <p className="lead text-neutral-500 md:text-start">
                                Most clinicians are live within a few days. The
                                only part that isn't instant is verification,
                                and that's the part that makes Welnido worth
                                being on.
                            </p>
                        </div>
                    </div>
                    <div className="col-span-full prose max-w-full space-y-4 prose-darkgreen">
                        <Card
                            size="lg"
                            className="bg-beige/50 ring-beige md:flex"
                        >
                            <div className="hidden pe-3 md:block">
                                <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-emerald-950 text-xl font-bold">
                                    1
                                </span>
                            </div>
                            <div>
                                <div className="flex flex-wrap items-center gap-3">
                                    <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-emerald-950 text-xl font-bold md:hidden">
                                        1
                                    </span>
                                    <h3 className="mt-0 mb-0 text-2xl">
                                        Create your account
                                    </h3>
                                    <div className="md:basis-wrap basis-full">
                                        <Badge variant="white" size="lg">
                                            5 minutes
                                        </Badge>
                                    </div>
                                </div>
                                <p className="lead">
                                    Tell us who you are, what you practise, and
                                    where. You pick your profession,
                                    specialities and services from our
                                    structured list, so patients searching for
                                    exactly what you do can actually find you.
                                </p>
                                <p className="mb-0 text-lg text-neutral-500">
                                    <i>
                                        Your profile isn't visible to anyone
                                        yet.
                                    </i>
                                </p>
                            </div>
                        </Card>
                        <Card size="lg" className="flex bg-beige/50 ring-beige">
                            <div className="hidden pe-3 md:block">
                                <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-emerald-950 text-xl font-bold">
                                    2
                                </span>
                            </div>
                            <div>
                                <div className="flex items-center gap-3">
                                    <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-emerald-950 text-xl font-bold md:hidden">
                                        2
                                    </span>
                                    <h3 className="mt-0 mb-0 text-2xl">
                                        Get verified with Credentially
                                    </h3>
                                    <div className="md:basis-wrap basis-full">
                                        <Badge variant="white" size="lg">
                                            usually 24 to 72 hours
                                        </Badge>
                                    </div>
                                </div>
                                <p className="lead">
                                    We hand you over to Credentially, the
                                    compliance platform used across UK
                                    healthcare, to verify your credentials
                                    properly. You'll be asked for:
                                </p>
                                <div className="grid max-w-full grid-cols-2 gap-4">
                                    <Card className="col-span-full flex gap-2 md:col-span-1 md:h-full">
                                        <CheckIcon className="mt-1 size-6 shrink-0 stroke-2 text-emerald-50" />{" "}
                                        <span>Photo ID and right to work</span>
                                    </Card>
                                    <Card className="col-span-full flex gap-2 md:col-span-1 md:h-full">
                                        <CheckIcon className="mt-1 size-6 shrink-0 stroke-2 text-emerald-50" />{" "}
                                        <span>
                                            Regulatory registration: GMC, NMC,
                                            HCPC, GDC, GPhC, GCC or GOsC
                                        </span>
                                    </Card>
                                    <Card className="col-span-full flex gap-2 md:col-span-1 md:h-full">
                                        <CheckIcon className="mt-1 size-6 shrink-0 stroke-2 text-emerald-50" />
                                        <span>
                                            A DBS certificate. We'll tell you
                                            which level, and point you to a
                                            check if yours has lapsed
                                        </span>
                                    </Card>
                                    <Card className="col-span-full flex gap-2 md:col-span-1 md:h-full">
                                        <CheckIcon className="mt-1 size-6 shrink-0 stroke-2 text-emerald-50" />
                                        <span>
                                            Professional indemnity insurance
                                        </span>
                                    </Card>
                                    <Card className="col-span-full flex gap-2 md:col-span-1 md:h-full">
                                        <CheckIcon className="mt-1 size-6 shrink-0 stroke-2 text-emerald-50" />
                                        <span>Proof of address</span>
                                    </Card>
                                    <Card className="col-span-full flex gap-2 md:col-span-1 md:h-full">
                                        <CheckIcon className="mt-1 size-6 shrink-0 stroke-2 text-emerald-50" />{" "}
                                        <span>Qualifications</span>
                                    </Card>
                                </div>
                                <p className="mb-0 text-lg text-neutral-500">
                                    <i>
                                        Upload once. Credentially checks your
                                        registration against the regulator's
                                        live register and keeps checking, so if
                                        anything is due to change or expire,
                                        you're alerted well before it lapses.
                                    </i>
                                </p>
                            </div>
                        </Card>
                        <Card size="lg" className="flex bg-beige/50 ring-beige">
                            <div className="hidden pe-3 md:block">
                                <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-emerald-950 text-xl font-bold">
                                    3
                                </span>
                            </div>
                            <div>
                                <div className="flex flex-wrap items-center gap-3">
                                    <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-emerald-950 text-xl font-bold md:hidden">
                                        3
                                    </span>
                                    <h3 className="mt-0 mb-0 text-2xl">
                                        Connect Stripe
                                    </h3>
                                    <div className="md:basis-wrap basis-full">
                                        <Badge variant="white" size="lg">
                                            5 minutes
                                        </Badge>
                                    </div>
                                </div>
                                <p className="lead">
                                    Welnido uses Stripe to take patient payments
                                    and pay you. You'll be sent to Stripe to
                                    confirm your identity and add the account
                                    you want paid into. We never see or hold
                                    your bank details.
                                </p>
                                <p className="mb-0 text-lg text-neutral-500">
                                    <i>
                                        Once connected, patient payments land in
                                        your Stripe balance and pay out to your
                                        account on Stripe's standard schedule.
                                    </i>
                                </p>
                            </div>
                        </Card>
                        <Card size="lg" className="flex bg-beige/50 ring-beige">
                            <div className="hidden pe-3 md:block">
                                <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-emerald-950 text-xl font-bold">
                                    4
                                </span>
                            </div>
                            <div>
                                <div className="flex items-center gap-3">
                                    <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-emerald-950 text-xl font-bold md:hidden">
                                        4
                                    </span>
                                    <h3 className="mt-0 mb-0 text-2xl">
                                        Set your availability and payment terms
                                    </h3>
                                </div>
                                <p className="lead mb-3">
                                    Add the hours you're open to being booked:
                                    by day, by location, by service. Block out
                                    anything you don't want touched. Nothing is
                                    bookable outside the windows you've set.
                                </p>
                                <p className="lead mt-0">
                                    Then choose how you want to be paid for each
                                    service. You set this, not us:
                                </p>
                                <p className="mb-0 text-lg text-neutral-500">
                                    <i>
                                        Set it differently for different
                                        services, and change it whenever you
                                        like.
                                    </i>
                                </p>
                            </div>
                        </Card>
                        <Card
                            size="lg"
                            className="flex bg-darkgreen-500 ring-darkgreen-500"
                        >
                            <div className="hidden pe-3 md:block">
                                <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-emerald-950 text-xl font-bold">
                                    5
                                </span>
                            </div>
                            <div>
                                <div className="flex items-center gap-3">
                                    <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-emerald-950 text-xl font-bold md:hidden">
                                        5
                                    </span>
                                    <h3 className="mt-0 mb-0 text-2xl text-white">
                                        Go live
                                    </h3>
                                </div>
                                <p className="lead mb-3 text-white/70">
                                    Your profile publishes and you become
                                    searchable.
                                </p>
                            </div>
                        </Card>
                    </div>
                </div>
            </div>
            <div className="flex h-full bg-beige px-4 pt-6 pb-24 md:px-10">
                <div className="container mx-auto grid grid-cols-12 gap-x-8 gap-y-6 md:pt-15">
                    <div className="col-span-full md:col-span-7">
                        <div className="mx-auto prose max-w-4xl prose-darkgreen">
                            <p className="text-sm font-medium tracking-[0.32em] text-neutral-400 uppercase md:text-start">
                                Bookings
                            </p>

                            <h2 className="mt-0 mb-4 text-4xl font-semibold tracking-wider md:text-start md:text-6xl">
                                How appointments actually work
                            </h2>

                            <p className="lead text-neutral-500 md:text-start">
                                You are never automatically booked. Every
                                appointment is a request until you say yes.
                            </p>
                        </div>
                    </div>
                    <div className="col-span-full prose max-w-full gap-4 space-y-4 prose-darkgreen md:flex">
                        <Card size="lg" className="block md:h-full md:w-1/4">
                            <p className="mt-0 text-sm font-medium tracking-[0.32em] text-neutral-400 uppercase md:text-start">
                                Step 1
                            </p>
                            <h3 className="mt-0 text-2xl">
                                A patient sends a request
                            </h3>
                            <p className="lead">
                                They pick a service and a slot from the
                                availability you've published, and pay on the
                                terms you've set for that service: in full, a
                                deposit, or nothing until the day.
                            </p>
                        </Card>
                        <Card size="lg" className="block md:h-full md:w-1/4">
                            <p className="mt-0 text-sm font-medium tracking-[0.32em] text-neutral-400 uppercase md:text-start">
                                Step 2
                            </p>
                            <h3 className="mt-0 text-2xl">You decide</h3>
                            <p className="lead">
                                A notification arrives by email and in your
                                dashboard. You can:
                            </p>
                            <ul className="list-none ps-0">
                                <li>
                                    <div className="flex items-start gap-2">
                                        <CheckIcon className="mt-1 size-4 shrink-0 stroke-2 text-emerald-50" />
                                        <span>
                                            <strong>Confirm:</strong> the is
                                            notified immediately and it's locked
                                            into both diaries
                                        </span>
                                    </div>
                                </li>
                                <li>
                                    <div className="flex items-start gap-2">
                                        <ArrowUturnUpIcon className="mt-1 size-4 shrink-0 stroke-2 text-emerald-50" />
                                        <span>
                                            <strong>
                                                Propose a different time:
                                            </strong>{" "}
                                            if the slot no longer works
                                        </span>
                                    </div>
                                </li>
                                <li>
                                    <div className="flex items-start gap-2">
                                        <XMarkIcon className="mt-1 size-4 shrink-0 stroke-2 text-emerald-50" />
                                        <span>
                                            <strong>Decline:</strong> the
                                            patient is refunded in full
                                        </span>
                                    </div>
                                </li>
                            </ul>
                        </Card>
                        <Card size="lg" className="block md:h-full md:w-1/4">
                            <p className="mt-0 text-sm font-medium tracking-[0.32em] text-neutral-400 uppercase md:text-start">
                                Step 3
                            </p>
                            <h3 className="mt-0 text-2xl">Reminders go out</h3>
                            <p className="lead">
                                Both sides get reminders ahead of the
                                appointment, and the patient can cancel or
                                reschedule alongside your cancellation terms.
                            </p>
                        </Card>
                        <Card size="lg" className="block md:h-full md:w-1/4">
                            <p className="mt-0 text-sm font-medium tracking-[0.32em] text-neutral-400 uppercase md:text-start">
                                Step 4
                            </p>
                            <h3 className="mt-0 text-2xl">
                                They rebook with you
                            </h3>
                            <p className="lead">
                                Repeat appointments go straight into your diary:
                                no queue, and no new-patient fee again.
                            </p>
                        </Card>
                    </div>
                </div>
            </div>
            <div className="flex h-full bg-white px-4 pt-6 pb-24 md:px-10">
                <div className="container mx-auto grid grid-cols-12 gap-x-8 gap-y-6 md:pt-15">
                    <div className="col-span-full md:col-span-7">
                        <div className="mx-auto prose max-w-4xl prose-darkgreen">
                            <p className="text-sm font-medium tracking-[0.32em] text-neutral-400 uppercase md:text-start">
                                Pricing
                            </p>

                            <h2 className="mt-0 mb-4 text-4xl font-semibold tracking-wider md:text-start md:text-6xl">
                                What you pay
                            </h2>

                            <p className="lead text-neutral-500 md:text-start">
                                Three charges. All of them visible before they
                                happen. No commission on your clinical fee.
                            </p>
                        </div>
                    </div>
                    <div className="col-span-full prose max-w-full gap-4 space-y-4 prose-darkgreen md:flex">
                        <Card
                            size="lg"
                            className="block bg-beige/50 ring-beige md:h-full md:w-1/3"
                        >
                            <h3 className="mt-0 text-2xl">Your subscription</h3>
                            <p>
                                <span className="text-5xl font-bold">
                                    £18.99{" "}
                                </span>
                                <span className="text-neutral-500">
                                    / month
                                </span>
                            </p>
                            <p className="lead">
                                Covers your verified profile, your listing, the
                                booking system, availability management and
                                ongoing credential monitoring. Cancel any time.
                            </p>
                        </Card>
                        <Card
                            size="lg"
                            className="block bg-beige/50 ring-beige md:h-full md:w-1/3"
                        >
                            <h3 className="mt-0 text-2xl">New patient fee</h3>
                            <p>
                                <span className="text-5xl font-bold">
                                    £9.99{" "}
                                </span>
                                <span className="text-neutral-500">
                                    / patient, once
                                </span>
                            </p>
                            <p className="lead">
                                Charged only when Welnido brought you that
                                patient, on the first appointment you confirm.
                                Every appointment with them afterwards is free
                                of it, for as long as they stay your patient.
                            </p>
                            <p className="mb-0 text-lg text-neutral-500">
                                <i>
                                    Bring your own patients onto Welnido and
                                    there's no new patient fee at all. We didn't
                                    find them, so we don't charge for them
                                </i>
                            </p>
                        </Card>
                        <Card
                            size="lg"
                            className="block bg-beige/50 ring-beige md:h-full md:w-1/3"
                        >
                            <h3 className="mt-0 text-2xl">Transaction fee</h3>
                            <p>
                                <span className="text-5xl font-bold">2% </span>
                                <span className="text-neutral-500">
                                    of platform payments
                                </span>
                            </p>
                            <p className="lead">
                                Applied only to money the patient pays through
                                the platform, covering card processing. Deducted
                                before payout, so what lands in your Stripe
                                balance is what you keep. Set a service to pay
                                on the day and there's nothing for us to apply
                                it to.
                            </p>
                        </Card>
                    </div>
                    <Card
                        size="lg"
                        className="block items-center bg-darkgreen-500 ring-darkgreen-500 md:flex"
                    >
                        <div className="prose prose-darkgreen">
                            <p className="mt-0 text-sm font-medium tracking-[0.32em] text-green-700 uppercase md:text-start">
                                A worked example
                            </p>
                            <h3 className="mt-2 mb-3 text-3xl font-semibold text-white">
                                A new patient books a £70 consultation and pays
                                in full.
                            </h3>
                            <p className="mb-4 text-white/70 md:mb-0">
                                You pay the £9.99 new patient fee and £1.40 in
                                transaction fees. Every appointment with them
                                after that costs you £1.40.
                            </p>
                        </div>
                        <Card
                            size="lg"
                            className="my-auto ms-auto gap-4 md:w-3/10 md:gap-5"
                        >
                            <div className="divide-y divide-neutral-200">
                                <div className="flex justify-between py-3">
                                    <span>Consultation fee</span>
                                    <span className="font-semibold">
                                        £70.00
                                    </span>
                                </div>
                                <div className="flex justify-between py-3">
                                    <span>New patient fee</span>
                                    <span className="font-semibold">
                                        - £9.99
                                    </span>
                                </div>
                                <div className="flex justify-between py-3">
                                    <span>Transaction fee (2%)</span>
                                    <span className="font-semibold">
                                        - £1.40
                                    </span>
                                </div>
                                <div className="flex justify-between py-3 text-xl">
                                    <strong>Your take</strong>
                                    <span className="font-semibold text-emerald-50">
                                        £58.61
                                    </span>
                                </div>
                            </div>
                        </Card>
                    </Card>
                    <Button size="lg" asChild>
                        <Link to="/register">List your practice</Link>
                    </Button>
                </div>
            </div>
        </>
    );
}

export default ForClinicians;
